// Setup › Workforce › Claude Code connection (`/c/{slug}/setup/claude-code`; WF-CONTROL phase 1,
// docs/runbooks/workforce-control-plugin.md): the Workforce Control add-on's switch (an owner or
// the agency operator; it needs Agents), a new connection (a manager with the add-on on: a label,
// then the pairing code shown once with the one command that pairs and registers the MCP server),
// and the caller's own connections with a hover revoke on each live or pending one. Labels stay
// short; explanations sit behind the (i).

import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Notice,
  SettingsSection,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CopyIcon, PlugIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDate, formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction, RowActions } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { useSession } from "../use-feature";
import { endpointUrl } from "../webhooks/incoming-webhooks";
import {
  type ControlToken,
  type ControlTokenCreated,
  controlKeys,
  controlTokensQuery,
  type PluginState,
  pairCommand,
  workforcePluginQuery,
} from "./queries";

const ON = new Set(["active", "trial", "grace"]);
const WRITABLE = new Set(["active", "trial"]);

const STATE_CHIP: Record<
  ControlToken["state"],
  { label: string; variant: "success" | "warning" | "neutral" | "danger" }
> = {
  pending: { label: "Pending", variant: "warning" },
  live: { label: "Live", variant: "success" },
  expired: { label: "Expired", variant: "neutral" },
  revoked: { label: "Revoked", variant: "danger" },
};

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.add({ title: `${what} copied`, type: "success" });
  } catch {
    toast.add({
      title: `Copy failed: select the ${what.toLowerCase()} and copy it`,
      type: "error",
    });
  }
}

export function ClaudeCodeScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const plugin = useQuery(workforcePluginQuery(api, company.id));
  return (
    <SetupPage title="Claude Code connection">
      {plugin.isPending ? (
        <Loading label="Loading Workforce Control" rows={4} />
      ) : plugin.isError ? (
        <LoadError what="Workforce Control" error={plugin.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-6" data-slot="claude-code">
          <AddonSection api={api} company={company} state={plugin.data} />
          <Connections api={api} company={company} state={plugin.data} />
        </div>
      )}
    </SetupPage>
  );
}

function AddonSection({
  api,
  company,
  state,
}: {
  api: Api;
  company: CompanyAccess;
  state: PluginState;
}) {
  const qc = useQueryClient();
  const on = ON.has(state.status);
  const id = useId();
  const put = useMutation({
    mutationFn: (enabled: boolean) =>
      api.client.workforcePlugin.put({ params: { co: company.id }, body: { enabled } }),
    onSuccess: async (next) => {
      qc.setQueryData(controlKeys.plugin(company.id), next);
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.add({
        title: ON.has(next.status) ? "Workforce Control is on" : "Workforce Control is off",
        type: "success",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not switched."), type: "error" }),
  });
  // Enabling needs Agents taking changes (not in read-only grace); switching off is always
  // possible for whoever may switch.
  const canToggle = state.can_switch && (on || state.agents_writable) && !put.isPending;
  return (
    <SettingsSection title="Add-on" data-slot="workforce-plugin">
      <div className="flex items-center justify-between gap-5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1">
            <label htmlFor={id} className="text-sm font-medium text-foreground">
              Workforce Control
            </label>
            <InfoTip label="About Workforce Control">
              Manage and train your digital employees and agents from Claude Code. Off by default;
              an owner or your CompanyOS operator switches it.
            </InfoTip>
            <Badge
              variant={on ? "success" : "neutral"}
              data-slot="workforce-plugin-status"
              data-status={state.status}
            >
              {state.status === "disabled"
                ? "Off"
                : state.status === "active"
                  ? "On"
                  : state.status === "trial"
                    ? "Trial"
                    : "Grace"}
            </Badge>
          </span>
          {!state.agents_on || (!on && !state.agents_writable) ? (
            <span className="text-xs text-muted-foreground" data-slot="workforce-plugin-needs">
              {state.agents_on ? "Agents is read-only" : "Needs Agents"}{" "}
              <Link
                to="/c/$slug/setup/addons"
                params={{ slug: company.slug }}
                hash="agents"
                className="underline underline-offset-2"
              >
                Add-ons
              </Link>
            </span>
          ) : !state.can_switch ? (
            <span className="text-xs text-muted-foreground" data-slot="workforce-plugin-owner">
              An owner switches it
            </span>
          ) : null}
        </div>
        <Switch
          id={id}
          checked={on}
          disabled={!canToggle}
          onCheckedChange={(next) => put.mutate(next)}
        />
      </div>
    </SettingsSection>
  );
}

function Connections({
  api,
  company,
  state,
}: {
  api: Api;
  company: CompanyAccess;
  state: PluginState;
}) {
  const q = useQuery(controlTokensQuery(api, company.id));
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ControlToken | null>(null);
  // New connections need a writable add-on (active or trial); grace is read-only.
  const writable = WRITABLE.has(state.status);
  const mayCreate = state.can_use && writable && state.agents_writable;
  const rows = q.data?.data ?? [];
  const open = rows.filter((t) => t.state === "live" || t.state === "pending").length;
  const atLimit = q.data ? open >= q.data.limit : false;
  const newButton = mayCreate ? (
    <Button size="sm" onClick={() => setCreating(true)} disabled={atLimit}>
      <PlusIcon data-icon="inline-start" />
      New connection
    </Button>
  ) : null;
  return (
    <SettingsSection
      title={
        <span className="flex items-center gap-1">
          Your connections
          <InfoTip label="About connections">
            Each connection is yours: a token for one Claude Code install, live 90 days, at most{" "}
            {q.data?.limit ?? 5} at a time. Revoke one to cut it off at once.
          </InfoTip>
        </span>
      }
      actions={newButton}
      data-slot="control-connections"
    >
      {state.status === "grace" || (state.agents_on && !state.agents_writable) ? (
        <p className="mb-2 text-xs text-muted-foreground" data-slot="control-read-only">
          Read-only: no new connections
        </p>
      ) : null}
      {atLimit && mayCreate ? (
        <p className="mb-2 text-xs text-muted-foreground" data-slot="control-limit">
          At the limit: revoke one first
        </p>
      ) : null}
      {q.isPending ? (
        <Loading label="Loading connections" rows={3} />
      ) : q.isError ? (
        <LoadError what="Connections" error={q.error} />
      ) : !rows.length ? (
        <EmptyState variant="compact" icon={PlugIcon} title="No connections yet" />
      ) : (
        <Table data-slot="control-tokens-table" className={DENSE_TABLE}>
          <TableCaption className="sr-only">Your Claude Code connections.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Label</TableHead>
              <TableHead scope="col">State</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                <span className="flex items-center gap-1">
                  MFA
                  <InfoTip label="About MFA">
                    Whether the person had two-step verification on when the connection was made.
                  </InfoTip>
                </span>
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Key
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Last used
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Expires
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id} data-control-token={t.id} className="group/row">
                <TableHead scope="row" className="min-w-0 font-medium">
                  <span className="truncate">{t.label}</span>
                </TableHead>
                <TableCell>
                  <Badge variant={STATE_CHIP[t.state].variant} data-state={t.state}>
                    {STATE_CHIP[t.state].label}
                  </Badge>
                </TableCell>
                <TableCell
                  className="text-muted-foreground max-md:hidden"
                  data-slot="control-token-mfa"
                  data-mfa={t.mfa}
                >
                  {t.mfa ? "MFA" : "No MFA"}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground max-md:hidden">
                  {t.key_prefix ? `${t.key_prefix}…` : "—"}
                </TableCell>
                <TableCell className="text-muted-foreground max-md:hidden">
                  {t.last_used_at ? formatDateTime(t.last_used_at) : "—"}
                </TableCell>
                <TableCell className="text-muted-foreground max-md:hidden">
                  {t.state === "pending" && t.pair_expires_at
                    ? `Code ${formatDateTime(t.pair_expires_at)}`
                    : t.state === "revoked"
                      ? "—"
                      : formatDate(t.expires_at)}
                </TableCell>
                <TableCell className="w-px">
                  {t.state === "live" || t.state === "pending" ? (
                    <RowActions>
                      <RowAction
                        icon={Trash2Icon}
                        label={`Revoke ${t.label}`}
                        tip="Revoke"
                        className="hover:text-danger-text"
                        onClick={() => setRevoking(t)}
                      />
                    </RowActions>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {creating ? (
        <NewConnectionDialog api={api} company={company} onClose={() => setCreating(false)} />
      ) : null}
      {revoking ? (
        <RevokeDialog
          api={api}
          company={company}
          token={revoking}
          onClose={() => setRevoking(null)}
        />
      ) : null}
    </SettingsSection>
  );
}

/** A refused new connection in plain words (the step-up's refusals named). */
function createError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "mfa_code_required")
      return "Code not accepted. Enter the current code from your authenticator app.";
    if (error.code === "rate_limited") return "Too many codes tried. Wait a few minutes.";
  }
  return problemText(error, "No code made.");
}

function NewConnectionDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const labelId = useId();
  const codeId = useId();
  const commandId = useId();
  // A person with two-factor sign-in confirms with a current code (a step-up on the request).
  const stepUp = useSession().me.mfa.enrolled;
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [made, setMade] = useState<ControlTokenCreated | null>(null);
  const totp = code.replace(/\s/g, "");
  const ready = Boolean(label.trim()) && (!stepUp || /^\d{6}$/.test(totp));
  const create = useMutation({
    mutationFn: () =>
      api.client.control.tokens.create({
        params: { co: company.id },
        body: { label: label.trim(), ...(stepUp ? { totp_code: totp } : {}) },
      }),
    onSuccess: (v) => {
      setMade(v);
      void qc.invalidateQueries({ queryKey: controlKeys.tokens(company.id) });
    },
    onError: () => setCode(""),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) create.mutate();
  };
  const origin = endpointUrl(api, "");
  const command = made ? pairCommand(origin, made.pair_code) : "";
  return (
    <Dialog open onOpenChange={(o) => !o && !create.isPending && onClose()}>
      <DialogContent className="sm:max-w-xl" data-slot="new-connection">
        <DialogHeader>
          <DialogTitle>{made ? "Pair Claude Code" : "New connection"}</DialogTitle>
        </DialogHeader>
        {!made ? (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor={labelId}>Label</FieldLabel>
              <Input
                id={labelId}
                value={label}
                maxLength={80}
                placeholder="Where it is used"
                autoFocus
                onChange={(e) => setLabel(e.target.value)}
              />
            </Field>
            {stepUp ? (
              <Field data-slot="step-up-code">
                <span className="flex items-center gap-1">
                  <FieldLabel htmlFor={codeId}>Code</FieldLabel>
                  <InfoTip label="About the code">
                    The 6-digit code from your authenticator app. You sign in with two-step
                    verification, so a new connection needs a current code too.
                  </InfoTip>
                </span>
                <Input
                  id={codeId}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  className="w-40 font-mono tracking-widest tabular-nums max-sm:w-full"
                  value={code}
                  aria-invalid={create.isError || undefined}
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
            ) : null}
            {create.isError ? (
              <Notice tone="danger" role="alert" data-slot="new-connection-error">
                {createError(create.error)}
              </Notice>
            ) : null}
            <DialogFooter>
              <DialogClose render={<Button variant="secondary" />} disabled={create.isPending}>
                Cancel
              </DialogClose>
              <Button type="submit" disabled={!ready || create.isPending}>
                {create.isPending ? "Making…" : "Make code"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex min-w-0 flex-col gap-4">
            <div className="flex flex-col gap-1">
              <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                Pairing code
                <InfoTip label="About the pairing code">
                  Shown once and works once. It expires 10 minutes after it was made; make a new one
                  if it lapses.
                </InfoTip>
              </span>
              <div className="flex items-center gap-2">
                <code
                  className="rounded-sm bg-sunken px-2 py-1 font-mono text-lg tracking-wider"
                  data-slot="pair-code"
                >
                  {made.pair_code}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void copyText(made.pair_code, "Code")}
                >
                  <CopyIcon aria-hidden="true" />
                  Copy
                </Button>
              </div>
              <span className="text-xs text-muted-foreground" data-slot="pair-expires">
                Expires {formatDateTime(made.pair_expires_at)}
              </span>
            </div>
            <Field>
              <span className="flex items-center gap-1">
                <FieldLabel htmlFor={commandId}>Command</FieldLabel>
                <InfoTip label="About the command">
                  Run it in a terminal where Claude Code is installed. It trades the code for this
                  connection's token and registers the CompanyOS MCP server with it; the token is
                  never shown here.
                </InfoTip>
              </span>
              <Textarea
                id={commandId}
                readOnly
                rows={4}
                value={command}
                className="font-mono text-xs"
                data-slot="pair-command"
                onFocus={(e) => e.currentTarget.select()}
              />
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void copyText(command, "Command")}
                >
                  <CopyIcon aria-hidden="true" />
                  Copy command
                </Button>
              </div>
            </Field>
            <DialogFooter>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({
  api,
  company,
  token,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  token: ControlToken;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: () =>
      api.client.control.tokens.revoke({ params: { co: company.id, id: token.id } }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: controlKeys.tokens(company.id) });
      toast.add({ title: "Connection revoked", type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && !revoke.isPending && onClose()}>
      <DialogContent data-slot="revoke-connection">
        <DialogHeader>
          <span className="flex items-center gap-1">
            <DialogTitle>Revoke {token.label}?</DialogTitle>
            <InfoTip label="About revoking">
              Claude Code on that install loses access at its next call. This cannot be undone; make
              a new connection to reconnect.
            </InfoTip>
          </span>
        </DialogHeader>
        {revoke.isError ? (
          <Notice tone="danger" role="alert">
            {problemText(revoke.error, "Not revoked.")}
          </Notice>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" />} disabled={revoke.isPending}>
            Cancel
          </DialogClose>
          <Button variant="destructive" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
            {revoke.isPending ? "Revoking…" : "Revoke"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
